import React, { useCallback, useEffect } from "react"
import styled, { css } from "styled-components"
import Breakpoints from "../../../components/shared/breakpoints"
import { Spacer } from "../../atoms/layout-primitives"
import Block from "../../atoms/layout-primitives/block"
import { BlogGrid } from "../../atoms/layout-primitives/grid"
import TagFilter from "../../molecules/tag-filter"
import { FadeIn } from "../../styles/animations.styles"
import { BT } from "../../styles/blog-typography"
import { IContentfulComposePage, IContentfulTag } from "../../types/blog.types"
import TopicRow from "../topic-row"

const FilterLabel = styled.label`
  display: block;
  ${BT.sm.info`500`};
  font-size: 12px;
  opacity: 0.45;
  text-transform: uppercase;
  @media (min-width: ${Breakpoints.md}) {
    display: none;
  }
`

export interface IArticleGroup {
  edges: {
    node: IContentfulComposePage
  }[]
  totalCount: number
  name: string
  slug: string
}

interface ITopicSectionProps {
  category: IContentfulTag
  articleGroups: IArticleGroup[]
}

/**
 * Template section to hold topic rows
 * @param category - The category of the topics
 * @param articleGroups - The articles grouped into their topics
 */
const CategoryBody = ({ category, articleGroups }: ITopicSectionProps) => {
  const [activeTopic, setActiveTopic] = React.useState("All")
  const [isAnimating, setIsAnimating] = React.useState(false)

  const handleTopicFilterClick = useCallback(
    (topicName: string) => {
      // Cancel current animation if one is still running
      setIsAnimating(false)

      // Delay a few milliseconds then run the animation again
      setTimeout(() => {
        setIsAnimating(true)

        // set active topic
        setActiveTopic(topicName)
      }, 10)
    },
    [setIsAnimating, setActiveTopic]
  )

  const onAnimationEnd = useCallback(() => {
    // Reset the animation at the end so it can be triggered again
    setIsAnimating(false)
  }, [setIsAnimating])

  useEffect(() => {
    // Add event listener to detect when animation is complete
    window.addEventListener("animationend", onAnimationEnd)
    return () => {
      window.removeEventListener("animationend", onAnimationEnd)
    }
  }, [])

  return (
    <BlogGrid>
      <Block>
        <FilterLabel htmlFor="topics">Filter Content</FilterLabel>
        <TagFilter
          tags={articleGroups}
          id="topics"
          name="Topics"
          onClick={handleTopicFilterClick}
        />
      </Block>
      <Spacer height={{ sm: "32px", md: "48px" }} />
      <Block
        variants={[
          css`
            animation-name: ${isAnimating ? FadeIn : "none"};
            animation-play-state: ${isAnimating ? "running" : "paused"};
            animation-duration: 0.5s;
          `,
        ]}
      >
        {articleGroups.map(({ edges, name, slug, totalCount }) => {
          return (
            <TopicRow
              key={`topic-${slug}`}
              category={category}
              topic={{ name: name, slug: slug }}
              articles={edges}
              isVisible={activeTopic === "All" || activeTopic === name}
              articleCount={totalCount}
            />
          )
        })}
      </Block>
    </BlogGrid>
  )
}

export default CategoryBody
